import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import * as y from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  createBirthDateSchema,
  createCPFOrCNPJSchema,
  createTestSchema,
} from '../shared/utils/schemas';
import {
  maskCellPhone,
  maskCPForCNPJ,
  maskDate,
  maskEmail,
  maskFullName,
  unmaskNumber,
} from '../shared/utils/masks';
import { validateFullName, validatePhone } from '../shared/utils/validation';
import { useSimuladorEnergiaSolarState } from './state';
import { usePostEntry } from '../api/use-post-entry';
import { getErrorData } from '../shared/utils/error';
import { getEnvAwareValue } from '../../../utils/browser';

const ValidationSchema = y.object().shape({
  document: createCPFOrCNPJSchema(),
  birthDate: createBirthDateSchema(),
  name: createTestSchema({ required: true, validate: validateFullName }),
  email: y.string().required('Campo obrigatório').email('E-mail inválido'),
  phone: createTestSchema({
    required: true,
    validate: validatePhone,
    messages: {
      required: 'Telefone é obrigatório',
      invalid: 'Telefone inválido',
    },
  }),
});

const InitialValues = {
  name: '',
  document: '',
  email: '',
  birthDate: '',
  phone: '',
};

export function useFormLanding() {
  const state = useSimuladorEnergiaSolarState();
  const register = usePostEntry({ step: 'start' });

  const form = useForm({
    values: InitialValues,
    resolver: yupResolver(ValidationSchema),
    shouldUseNativeValidation: false,
    criteriaMode: 'firstError',
    disabled: state.simulationExclusiveView,
  });

  const documentValue = form.watch('document');

  const fields = useMemo(() => {
    const fieldDefinition = {
      name: { label: 'Nome completo', maskFunction: maskFullName },
      document: { label: 'CPF ou CNPJ', maskFunction: maskCPForCNPJ, inputMode: 'numeric' },
      birthDate: { label: 'Data de nascimento', maskFunction: maskDate, inputMode: 'numeric' },
      phone: { label: 'Telefone', maskFunction: maskCellPhone, type: 'tel' },
      email: {
        label: 'E-mail',
        type: 'email',
        maskFunction: maskEmail,
        handleValueChange: (value) =>
          form.setValue('email', value, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          }),
      },
    };

    if (!!documentValue && unmaskNumber(documentValue).length > 11) {
      fieldDefinition.birthDate.label = 'Data de fundação';
    }

    return Object.entries(fieldDefinition).map(([name, value]) => ({ ...value, name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentValue]);

  /** @type {import('react-hook-form').SubmitHandler<typeof InitialValues>} */
  const handleSubmit = async (data) => {
    try {
      const response = await register.mutateAsync(data);

      state.goToSimulationStep({
        ...response,
        formLanding: data,
      });

      window.location.href = getEnvAwareValue(
        './energia-solar-fotovoltaica-orcamento.html',
        './energia-solar-fotovoltaica-orcamento',
      );
    } catch (error) {
      state.showErrorStep({
        ...getErrorData(error),
        modal: true,
        scrollTo: '#form-energia-solar',
        ctx: 'api-landing-form',
      });
    }
  };

  return {
    form,
    fields,
    handleSubmit: form.handleSubmit(handleSubmit),
    loading: register.isPending,
  };
}
